import React, { useEffect, useState } from "react";
import { Table, Avatar, Typography, Input, Tag, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  CalendarOutlined,
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  TeamOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import moment from "moment";
import { getConsultantList } from "../../../redux/consultant/consultantAction";
import { useGetConsultantListQuery } from "../../../redux/rtk/apiRtk";
import { motion } from "framer-motion";
import { primaryColor } from "../../../utils/constants";

const { Title, Text } = Typography;

const ConsultantProfile = () => {
  // Theme color

  // State
  const [consultants, setConsultants] = useState([]);
  const [filteredConsultants, setFilteredConsultants] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [animateTable, setAnimateTable] = useState(false);

  // API hooks
  const { data, isLoading } = useGetConsultantListQuery();
  const consultantList = useSelector(
    (state) => state.consultant.consultantList
  );
  const dispatch = useDispatch();

  // Fetch data
  useEffect(() => {
    dispatch(getConsultantList());
  }, [dispatch]);

  // Update state when data changes
  useEffect(() => {
    if (data) {
      setConsultants(data);
      setFilteredConsultants(data);
    }
  }, [data, isLoading]);

  // Trigger animation after component mounts
  useEffect(() => {
    setTimeout(() => {
      setAnimateTable(true);
    }, 100);
  }, []);

  // Search functionality
  const handleSearch = (value) => {
    setSearchText(value);

    if (!value) {
      setFilteredConsultants(consultants);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = consultants.filter(
      (item) =>
        (item.firstName &&
          item.firstName.toLowerCase().includes(searchLower)) ||
        (item.lastName && item.lastName.toLowerCase().includes(searchLower)) ||
        (item.phone && item.phone.includes(value)) ||
        (item.availability &&
          item.availability.some((avail) =>
            avail.toLowerCase().includes(searchLower)
          ))
    );

    setFilteredConsultants(filtered);
  };

  // Table columns
  const columns = [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <TeamOutlined style={{ color: primaryColor }} />
          </div>
          <span>ID</span>
        </div>
      ),
      dataIndex: "id",
      key: "id",
      width: "5%",
      render: (_, record, index) => <Text strong>{index + 1}</Text>,
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>CONSULTANT</span>
        </div>
      ),
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (_, record) => (
        <div className="flex items-center">
          {record.image ? (
            <Avatar
              size={40}
              src={record.image}
              className="border-2 border-white shadow-sm"
              style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
            />
          ) : (
            <Avatar
              size={40}
              icon={<UserOutlined />}
              className="border-2 border-white shadow-sm"
              style={{
                backgroundColor: `${primaryColor}50`,
                color: primaryColor,
              }}
            />
          )}
          <div className="ml-3">
            <Text strong className="block text-gray-800">
              {record.firstName} {record.lastName}
            </Text>
            {record.category && record.category.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {record.category.slice(0, 2).map((cat, i) => (
                  <Tag
                    key={i}
                    className="text-xs capitalize px-2 py-0"
                    style={{
                      backgroundColor: `${primaryColor}08`,
                      color: primaryColor,
                      border: `1px solid ${primaryColor}15`,
                    }}
                  >
                    {cat}
                  </Tag>
                ))}
                {record.category.length > 2 && (
                  <Tooltip title={record.category.slice(2).join(", ")}>
                    <Tag
                      className="text-xs px-2 py-0"
                      style={{
                        backgroundColor: `${primaryColor}08`,
                        color: primaryColor,
                        border: `1px solid ${primaryColor}15`,
                      }}
                    >
                      +{record.category.length - 2}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <PhoneOutlined style={{ color: primaryColor }} />
          </div>
          <span>PHONE</span>
        </div>
      ),
      dataIndex: "phone",
      key: "phone",
      width: "15%",
      render: (phone) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}10` }}
          >
            <PhoneOutlined style={{ color: primaryColor, fontSize: "12px" }} />
          </div>
          <Text>{phone}</Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <DollarOutlined style={{ color: primaryColor }} />
          </div>
          <span>PRICE/MIN</span>
        </div>
      ),
      dataIndex: "pricePerMinute",
      key: "pricePerMinute",
      width: "12%",
      render: (price) => (
        <Tag
          className="text-sm px-3 py-1 font-medium"
          style={{
            backgroundColor: `${primaryColor}15`,
            color: primaryColor,
            border: `1px solid ${primaryColor}30`,
          }}
        >
          ₹{price}
        </Tag>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CheckCircleFilled style={{ color: primaryColor }} />
          </div>
          <span>STATUS</span>
        </div>
      ),
      dataIndex: "isBlocked",
      key: "status",
      width: "10%",
      render: (isBlocked) =>
        isBlocked ? (
          <div className="flex items-center">
            <CloseCircleFilled className="mr-2" style={{ color: "#f5222d" }} />
            <Text type="danger" strong>
              Blocked
            </Text>
          </div>
        ) : (
          <div className="flex items-center">
            <CheckCircleFilled className="mr-2" style={{ color: "#52c41a" }} />
            <Text type="success" strong>
              Active
            </Text>
          </div>
        ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>CREATED</span>
        </div>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: "15%",
      render: (date) => (
        <div>
          <Text className="block">{moment(date).format("MMM Do, YYYY")}</Text>
          <Text type="secondary" className="text-xs">
            {moment(date).format("h:mm A")}
          </Text>
        </div>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Link to={`/consultant-details/${record._id}`}>
          <button
            className="flex items-center px-3 py-1.5 rounded-lg transition-all duration-300 hover:shadow-md"
            style={{
              background: primaryColor,
              color: "white",
              boxShadow: `0 2px 8px 0 ${primaryColor}40`,
            }}
          >
            <EyeOutlined className="mr-1" /> View Profile
          </button>
        </Link>
      ),
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-4 sm:p-6 lg:p-8"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: animateTable ? 1 : 0,
          y: animateTable ? 0 : 20,
        }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        {/* Header */}
        <div
          className="py-5 px-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
          style={{ borderColor: "rgba(164, 1, 101, 0.1)" }}
        >
          <div className="flex items-center">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full mr-4"
              style={{ background: `${primaryColor}15` }}
            >
              <TeamOutlined style={{ fontSize: 20, color: primaryColor }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: primaryColor }}>
                Consultant Management
              </Title>
              <Text type="secondary">
                {filteredConsultants.length} consultants found
              </Text>
            </div>
          </div>

          <div className="custom-search-wrapper" style={{ width: 300 }}>
            <Input
              placeholder="Search by name or phone..."
              prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
              allowClear
              onChange={(e) => handleSearch(e.target.value)}
              className="custom-search-input"
              style={{
                borderRadius: "8px",
                padding: "8px 12px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="p-0">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Spin
                size="large"
                tip="Loading consultants..."
                style={{ "--ant-primary-color": primaryColor }}
              />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredConsultants.map((item) => ({
                ...item,
                key: item._id,
              }))}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} consultants`,
                className: "px-6 py-4",
              }}
              className="custom-table"
              rowClassName={() => "table-row"}
              scroll={{ x: 1000 }}
              locale={{
                emptyText: (
                  <div className="py-8 text-center">
                    <UserOutlined style={{ fontSize: 32, color: "#ccc" }} />
                    <p className="mt-2 text-gray-500">
                      {searchText
                        ? "No consultants match your search"
                        : "No consultants found"}
                    </p>
                  </div>
                ),
              }}
            />
          )}
        </div>
      </motion.div>

      {/* Custom styles */}
      <style jsx="true">{`
        .custom-search-input {
          border-color: #e2e8f0;
        }

        .custom-search-input:hover {
          border-color: ${primaryColor}80 !important;
        }

        .custom-search-input:focus,
        .custom-search-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .custom-table .ant-table-thead > tr > th {
          background-color: #f9f9f9;
          font-weight: 600;
          color: #333;
          padding: 16px;
        }

        .table-row:hover {
          background-color: ${primaryColor}05 !important;
        }

        .ant-table-row-expand-icon {
          color: ${primaryColor} !important;
        }

        .ant-pagination-item-active {
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-item-active a {
          color: ${primaryColor} !important;
        }

        .ant-select-item-option-selected {
          background-color: ${primaryColor}15 !important;
        }
      `}</style>
    </motion.div>
  );
};

export default ConsultantProfile;
